import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { EmailStartDto, PublicBrandDto, SignedInDto } from '@fyr/shared';
import { api, ApiError } from '../api';
import { addPasskey, isCancelled, passkeysSupported, signInWithPasskey } from '../passkeys';
import { applyBrand } from '../theme';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ReleaseBadge } from '../components/ReleaseBadge';

interface DevPerson {
  id: string;
  displayName: string;
  email: string;
  status: 'pending' | 'active';
  defaultAffiliation: string | null;
  contexts: string | null;
}

type Step =
  | { at: 'start' }
  | { at: 'code'; email: string; sent: EmailStartDto & { challengeId: string } }
  | { at: 'sso'; organisation: string }
  | { at: 'offer'; to: string };

/**
 * The front door. #147: a passkey, or an emailed code (which is also how someone new signs up); SSO for those whose
 * organisation uses it. The dev picker only exists when the api runs with AUTH_DEV_LOGIN outside production.
 */
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const cfg = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<{ devLogin: boolean; oidc: boolean; emailSignIn: boolean }>('/auth/config'),
  });
  const devPeople = useQuery({
    queryKey: ['dev-people'],
    queryFn: () => api.get<DevPerson[]>('/auth/dev-people'),
    enabled: !!cfg.data?.devLogin,
  });
  const error = params.get('error');
  // §17 /login?org=<slug>: that organisation's sign-in page (only if its admin switched the public link on).
  const org = params.get('org');
  const brand = useQuery({
    queryKey: ['public-brand', org],
    queryFn: () => api.get<PublicBrandDto>(`/public/brand/${encodeURIComponent(org!)}`),
    enabled: !!org && /^[a-z0-9-]{1,64}$/.test(org),
    retry: false,
  });
  const b = brand.data;
  useEffect(() => {
    if (b) applyBrand({ primary: b.primary, accent: b.primary, faviconAssetId: null });
    return () => applyBrand(null);
  }, [b]);

  const [step, setStep] = useState<Step>({ at: 'start' });
  // #153 an invite's link says which address to use
  const [email, setEmail] = useState(() => params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const attempt = async (fn: () => Promise<void>) => {
    setProblem(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      if (!isCancelled(e)) setProblem(e instanceof ApiError ? e.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  };
  const signedIn = (r: SignedInDto, offerPasskey: boolean) => {
    qc.clear();
    const to = r.pending ? '/pending' : '/';
    // signed in by email on a device that can hold a passkey: offer one, so next time is a single tap
    if (offerPasskey && passkeysSupported()) setStep({ at: 'offer', to });
    else nav(to);
  };

  async function devLogin(personId: string) {
    const r = await api.post<{ pending: boolean }>('/auth/dev-login', { personId });
    qc.clear();
    nav(r.pending ? '/pending' : '/');
  }

  return (
    <div className="login">
      <div className="login-card">
        <div className={`login-head${b ? ' branded' : ''}`}>
          {b?.logoUrl ? (
            <img className="login-logo" src={b.logoUrl} alt={b.name} />
          ) : (
            <img src="/favicon.svg" alt="" width={36} height={36} />
          )}
          <div>
            <h1>
              {b ? t('login.titleFor', { org: b.name }) : t('login.title')} <ReleaseBadge />
            </h1>
            <p className="muted">{b ? t('login.poweredBy') : t('app.tagline')}</p>
          </div>
          <LanguageSwitcher />
        </div>
        {b?.banner && <div className="login-banner">{b.banner}</div>}
        {error && (
          <div className="alert">{t(`login.error.${error}`, { defaultValue: t('login.error.sso') })}</div>
        )}
        {problem && (
          <p className="alert" role="alert">
            {problem}
          </p>
        )}
        {step.at === 'sso' ? (
          <p role="status">{t('login.toOrgSso', { org: step.organisation })}</p>
        ) : step.at === 'offer' ? (
          <div className="login-offer" role="status">
            <p>
              <strong>{t('login.offerTitle')}</strong>
            </p>
            <p className="muted small">{t('login.offerBody')}</p>
            <div className="row">
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() =>
                  void attempt(async () => {
                    await addPasskey();
                    nav(step.to);
                  })
                }
              >
                {t('login.offerAdd')}
              </button>
              <button className="btn" onClick={() => nav(step.to)}>
                {t('login.offerLater')}
              </button>
            </div>
          </div>
        ) : step.at === 'code' ? (
          <form
            className="login-email"
            onSubmit={(e) => {
              e.preventDefault();
              void attempt(async () => {
                const r = await api.post<SignedInDto>('/auth/email/verify', {
                  challengeId: step.sent.challengeId,
                  email: step.email,
                  code,
                  name: name.trim() || null,
                });
                signedIn(r, true);
              });
            }}
          >
            <p>{t('login.codeSent', { email: step.email })}</p>
            {step.sent.sso && (
              <a className="btn btn-block" href={step.sent.sso.url}>
                {t('login.orgSso', { org: step.sent.sso.organisation })}
              </a>
            )}
            {step.sent.devCode && (
              <p className="muted small">{t('login.devCode', { code: step.sent.devCode })}</p>
            )}
            <label className="field">
              <span>{t('login.code')}</span>
              <input
                className="text-input login-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]{6,7}"
                maxLength={7}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <label className="field">
              <span>
                {t('login.name')} <span className="muted small">{t('login.nameHint')}</span>
              </span>
              <input
                className="text-input"
                autoComplete="name"
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
              {t('login.verify')}
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => {
                setCode('');
                setStep({ at: 'start' });
              }}
            >
              {t('login.differentEmail')}
            </button>
          </form>
        ) : (
          <>
            {cfg.data?.emailSignIn && passkeysSupported() && (
              <button
                className="btn btn-primary btn-block"
                disabled={busy}
                onClick={() => void attempt(async () => signedIn(await signInWithPasskey(), false))}
              >
                {t('login.passkey')}
              </button>
            )}
            {cfg.data?.emailSignIn && (
              <form
                className="login-email"
                onSubmit={(e) => {
                  e.preventDefault();
                  void attempt(async () => {
                    const sent = await api.post<EmailStartDto>('/auth/email/start', {
                      email,
                      locale: i18n.language,
                    });
                    // #147 M20.2 the address's organisation signs its people in with its own SSO: go there
                    if (sent.ssoRequired && sent.sso) {
                      setStep({ at: 'sso', organisation: sent.sso.organisation });
                      window.location.assign(sent.sso.url);
                    } else if (sent.challengeId)
                      setStep({
                        at: 'code',
                        email: email.trim(),
                        sent: { ...sent, challengeId: sent.challengeId },
                      });
                  });
                }}
              >
                <label className="field">
                  <span>{t('login.email')}</span>
                  <input
                    className="text-input"
                    type="email"
                    autoComplete="email webauthn"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <button className="btn btn-block" type="submit" disabled={busy}>
                  {t('login.emailContinue')}
                </button>
                <p className="muted small">{t('login.emailHint')}</p>
              </form>
            )}
            {cfg.data?.oidc ? (
              <a
                className={`btn btn-block ${cfg.data.emailSignIn ? '' : 'btn-primary'}`}
                href="/api/auth/login"
              >
                {t('login.sso')}
              </a>
            ) : (
              !cfg.data?.emailSignIn && (
                <>
                  <span className="btn btn-primary btn-block disabled">{t('login.sso')}</span>
                  <p className="muted small">{t('login.ssoNotConfigured')}</p>
                </>
              )
            )}
            {cfg.data?.oidc && <p className="muted small">{t('login.ssoHint')}</p>}
          </>
        )}
        {cfg.data?.devLogin && step.at === 'start' && (
          <div className="dev-login">
            <h2>{t('login.devTitle')}</h2>
            <p className="muted small">{t('login.devHint')}</p>
            <ul>
              {devPeople.data?.map((p) => (
                <li key={p.id}>
                  <button className="dev-user" onClick={() => void devLogin(p.id)}>
                    <strong>{p.displayName}</strong>
                    {p.status === 'pending' ? (
                      <span className="badge aff-pending">{t('login.pendingBadge')}</span>
                    ) : (
                      <span className="badge">
                        {t('login.contexts', {
                          count: (p.contexts ?? '').split(' · ').filter(Boolean).length,
                        })}
                      </span>
                    )}
                    <span className="muted small">
                      {p.email}
                      {p.contexts ? ` · ${p.contexts}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
